import { useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Lock, MoreVertical } from 'lucide-react';
import { useClickOutside } from '../../lib/useClickOutside';
import type { SurveyStep } from '../../types/survey';
import { STEP_TYPE_ICON, STEP_TYPE_LABEL, branchingSummary, summarizeStep } from './stepSummary';

// Phase 6B B3 — a step card. Normal steps are draggable (SortableStepCard);
// the auto-inserted name step is rendered locked (LockedStepCard: no drag, no
// delete, "System question" badge). The kebab exposes Edit / Duplicate /
// Delete; Edit opens the per-type editor (wired in B4).

type CardActions = {
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

function CardBody({
  step,
  dragHandle,
  locked,
  actions,
}: {
  step: SurveyStep;
  dragHandle?: React.ReactNode;
  locked?: boolean;
  actions?: CardActions;
}) {
  const Icon = STEP_TYPE_ICON[step.type];
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3">
      <div className="flex h-8 w-6 items-center justify-center text-slate-300">
        {locked ? <Lock className="h-4 w-4" /> : dragHandle}
      </div>
      <Icon className="h-5 w-5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
            {STEP_TYPE_LABEL[step.type]}
          </span>
          {step.kind === 'question' && step.required && (
            <span className="text-xs font-medium text-amber-600">Required</span>
          )}
          {locked && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
              System question
            </span>
          )}
        </div>
        <p className="truncate text-sm text-slate-800">{summarizeStep(step)}</p>
        <p className="mt-0.5 text-xs text-slate-400">{branchingSummary(step)}</p>
      </div>
      {locked ? (
        <button
          type="button"
          onClick={actions?.onEdit}
          className="rounded px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100"
        >
          View
        </button>
      ) : (
        actions && <KebabMenu actions={actions} />
      )}
    </div>
  );
}

export function SortableStepCard({
  step,
  onEdit,
  onDuplicate,
  onDelete,
}: { step: SurveyStep } & CardActions) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const handle = (
    <button
      type="button"
      className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
      aria-label="Drag to reorder"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style}>
      <CardBody step={step} dragHandle={handle} actions={{ onEdit, onDuplicate, onDelete }} />
    </div>
  );
}

export function LockedStepCard({ step, onEdit }: { step: SurveyStep; onEdit: () => void }) {
  return (
    <CardBody
      step={step}
      locked
      actions={{ onEdit, onDuplicate: () => undefined, onDelete: () => undefined }}
    />
  );
}

function KebabMenu({ actions }: { actions: CardActions }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const item = (label: string, onClick: () => void, danger = false) => (
    <button
      type="button"
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      className={
        'block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ' +
        (danger ? 'text-red-600' : 'text-slate-700')
      }
    >
      {label}
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        aria-label="Step actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-32 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {item('Edit', actions.onEdit)}
          {item('Duplicate', actions.onDuplicate)}
          {item('Delete', actions.onDelete, true)}
        </div>
      )}
    </div>
  );
}
